// Источник прихода (первое касание) — общий для клиента и сервера, без API Deno/браузера.
//
// Сайт: ?utm_source=tiktok&utm_campaign=v045&g=<игра>  ·  ?ref=<код игрока>  ·  ?qr=<код>  ·  ?p=<партнёр>
//   (короткие формы тоже: s, m, c, k = utm_source/medium/campaign/content).
// Telegram (start_param, только [A-Za-z0-9_-], ≤ 64): пары «ключ-значение» через «__»:
//   g-<игра>__s-tiktok__c-v045 · r-<код игрока> · q-<код> · p-<партнёр> · e-pk (пришёл из приложения экосистемы).
// Сайт: ?from=pk — то же, что e-pk.
// Тип: r → ref, q → qr, p → partner, e → ecosystem (source = код приложения, s → medium), есть source → utm, только g → link, ничего → organic.

export type SourceKind = 'utm' | 'qr' | 'ref' | 'partner' | 'ecosystem' | 'link' | 'organic';
export type Source = {
  kind: SourceKind;
  source?: string; medium?: string; campaign?: string; content?: string;
  ref?: string; partner?: string; landing?: string;
};

const SAFE = /^[A-Za-z0-9._-]{1,100}$/;
const clean = (v: string | null | undefined) => (v && SAFE.test(v) ? v : undefined);

const LONG: Record<string, keyof Source> = {
  utm_source: 'source', utm_medium: 'medium', utm_campaign: 'campaign', utm_content: 'content',
  s: 'source', m: 'medium', c: 'campaign', k: 'content',
  ref: 'ref', r: 'ref', qr: 'qr' as keyof Source, q: 'qr' as keyof Source, p: 'partner', partner: 'partner', g: 'landing',
  e: 'eco' as keyof Source, from: 'eco' as keyof Source,
};
const ECOSYSTEM = new Set(['pk', 'bfc']);

function build(get: (k: string) => string | undefined): Source {
  const v: Record<string, string | undefined> = {};
  for (const [k, field] of Object.entries(LONG)) {
    const x = clean(get(k));
    if (x && !v[field]) v[field] = x;
  }
  const qr = v.qr;
  const eco = v.eco && ECOSYSTEM.has(v.eco.toLowerCase()) ? v.eco.toLowerCase() : undefined;
  const kind: SourceKind = v.ref ? 'ref' : qr ? 'qr' : v.partner ? 'partner' : eco ? 'ecosystem' : v.source ? 'utm' : v.landing ? 'link' : 'organic';
  const out: Source = { kind };
  if (kind === 'qr') out.source = qr;
  if (kind === 'ecosystem') { out.source = eco; if (v.source) out.medium = v.source; }
  for (const f of ['source', 'medium', 'campaign', 'content', 'ref', 'partner', 'landing'] as const) if (v[f] && !out[f]) out[f] = v[f];
  return out;
}

export function sourceFromQuery(search: string | URLSearchParams): Source {
  const q = typeof search === 'string' ? new URLSearchParams(search) : search;
  return build(k => q.get(k) ?? undefined);
}

export function sourceFromStartParam(sp: string | null | undefined): Source {
  if (!sp || !/^[A-Za-z0-9_-]{1,64}$/.test(sp)) return { kind: 'organic' };
  const pairs = new Map<string, string>();
  for (const part of sp.split('__')) {
    const i = part.indexOf('-');
    if (i > 0) pairs.set(part.slice(0, i), part.slice(i + 1));
  }
  return build(k => (k.length === 1 ? pairs.get(k) : undefined));
}

// обратное — для ссылок «поделиться» и рекламы в Telegram
export function startParam(s: Partial<Record<'g' | 's' | 'c' | 'r' | 'q' | 'p' | 'e', string>>): string {
  return Object.entries(s).filter(([, v]) => v).map(([k, v]) => `${k}-${v}`).join('__').slice(0, 64);
}

export const hasSource = (s: Source) => s.kind !== 'organic';
